import { GuardProvider } from './auth';
import { useMemo, useState, type ReactNode } from "react";
import { Provider, useDispatch, useSelector } from "react-redux";
import ArtStore, { setUser, updateAppState, updateLicenseState } from "./store";
import type { RootState } from "./store";
import { ArtServiceProvider, APIService, ArtAppLoader } from "@art/shared-ui";
import AdminLayout from "./components/layout/admin-layout";
import ArtLiveLoader from "./global/remote-loaders/art-live-loader";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import ArtEnvironmentSettingsPage from "./global/settings/environment/list";
import ArtProjectPage from "./global/settings/projects/list";
import ProjectAccessDetails from "./global/settings/projects/details";
import GeneralSettings from "./global/settings/general/general-settings";
import AppSettings from "./global/settings/general/general";
import EmailConfigEditor from "./global/settings/email-config/email-config-editor";
import { EmailConfigListContent } from "./global/settings/email-config/list";
import ProjectTemplates from "./global/settings/email-config/project-templates";
import EmailTemplateEditor from "./global/settings/email-config/template-editor";
import EmailConfigOAuthCallback from "./global/settings/email-config/oauth-callback";
import PersonalProfile from "./global/settings/general/personal-profile";
import RolesPermissions from "./global/settings/roles-permissions";
import { ArtConnectionList } from "./global/settings/connection/list";
import { ArtProfileInsight } from "./global/settings/connection/profile-insight/[[...id]]";
import { GroupList } from "./global/settings/groups/list";
import { SessionRolesList } from "./global/settings/roles/list";
import RoleDetail from "./global/settings/roles-permissions/role-detail";
import RoleDetailInfo from "./global/settings/roles-permissions/role-detail/details";
import RoleAbac from "./global/settings/roles-permissions/role-detail/abac";
import RoleResourceAccess from "./global/settings/roles-permissions/role-detail/resource-access";
import { PermissionsProvider, RequireAttributePermission, RequirePermission, useAttributePermission, usePermissionsContext } from "./global/permissions";
import { AppSwitchProvider } from "./components/header/app-switch/app-switch";
import AccountsSettings from "./global/settings/general/accounts";
import LinkedDevices from "./global/settings/general/linked-devices";
import Workspaces from "./global/settings/general/workspaces";
import ArtUserSettingsPage from "./global/settings/application-users/list";
import ApplicationClientSettings from "./global/settings/client/list";
import APIKeyManagement from "./global/settings/api-keys";
import { licenseStateFromProfile } from "./utils/license";
import { applicationBrandForPath } from "./utils/application-brand";
import MessagingConfiguration from "./global/settings/messaging-configuration";
import PlatformLimits from "./global/settings/usage/platform-limits";
import GeneralUsage from "./global/settings/usage/general-usage";
import AppUsageAlert from "./global/settings/usage/alert/alert";
import GeneralPlanSettings from "./global/settings/billing-settings";
import ArtBillingOverview from "./global/settings/billing-settings/overview";
import ArtBillingPreferences from "./global/settings/billing-settings/billing-preferences";
import AddonsPage from "./global/settings/billing-settings/addons";
import ArtBillingHistory from "./global/settings/billing-settings/billing-history";
import { ART_URL } from "./Constant";
import {
  classifyProfileBootstrapError,
  PROFILE_BOOTSTRAP_RETRY_DELAYS_MS,
  shouldRetryProfileBootstrapError,
  type ProfileBootstrapStatus,
} from "./auth/profile-bootstrap";
import OnboardingFlow from "./components/on-boarding";
import { SnackbarProvider as NSProvider, SnackbarProviderProps, } from "notistack";
import SnackbarContainer from "./components/layout/SnackbarContainer";
import ArtSuperAdminLoader from "./global/remote-loaders/art-super-admin-loader";
import GetStarted from "./components/on-boarding/steps/get-started";
import ProjectSetup from "./components/on-boarding/steps/project-setup";
import CompleteSetup from "./components/on-boarding/steps/complete-setup";
import NavGuide from "./components/on-boarding/steps/nav-guide";
import AuthCheck from "./components/auth-check/auth-check";
// import ComingSoon from "./components/coming-soon"; // only used by the Marketplace route
import ProductSettingsLoader from "./global/remote-loaders/product-settings-loader";

import "bootstrap/dist/css/bootstrap.min.css";
import "./App.scss";


if (
  window.location.pathname.includes("/email-config/oauth/callback") &&
  (window.location.search.includes("code=") || window.location.search.includes("error="))
) {
  const search = window.location.search;
  const sp = new URLSearchParams(search);
  const isPopup = Boolean(window.opener && window.opener !== window) || window.name === "art-google-oauth";
  if (isPopup) {
    const payload = {
      type: "email_oauth_callback",
      code: sp.get("code"),
      state: sp.get("state"),
      error: sp.get("error"),
    };
    try {
      new BroadcastChannel("email_oauth_callback").postMessage(payload);
    } catch { /* very old browser — opener fallback below */ }
    try {
      window.opener?.postMessage(payload, window.location.origin);
    } catch { /* opener severed — BroadcastChannel already delivered */ }
    window.close();
  } else if (search.includes("code=")) {
    sessionStorage.setItem("email_oauth_callback", search);
  }
}
import ArtAgentBuilderLoader from "./global/remote-loaders/art-agent-builder-loader";
// import { CookbookMarketplace } from "./global/marketplace-v2/cookbook-marketplace";
// import { RecipeDetail } from "./global/marketplace-v2/recipe-detail";
// import ArtAppStudioLoader from "./global/remote-loaders/art-app-studio-loader";
import ApplicationUsers from "./global/settings/application-users";
import InviteUser from "./global/settings/application-users/invite-user";
import { PersistGate } from 'redux-persist/integration/react'
import { persistor } from './store'
import { getPlatformSettingsLanding } from "./global/settings/platform-settings-landing";
import AccessDenied from "./components/access-denied";
import ApplicationUnavailable from "./components/application-unavailable";
import ArtDatalakeLoader from "./global/remote-loaders/art-datalake-loader";

const SnackbarProvider: React.FC<SnackbarProviderProps> = (props) => {
  const Provider =
    NSProvider as unknown as React.ComponentType<SnackbarProviderProps>;
  return <Provider {...props}>{props.children}</Provider>;
};

function ApplicationUsersIndex() {
  const canViewUsers = useAttributePermission("PLATFORM_SETTINGS", "USER_MANAGEMENT", "ViewUsers");
  const canViewInvites = useAttributePermission("PLATFORM_SETTINGS", "USER_MANAGEMENT", "ViewInvitations");
  if (canViewUsers) return <Navigate to="/platform-settings/application-users/users" replace />;
  if (canViewInvites) return <Navigate to="/platform-settings/application-users/invite-users" replace />;
  return <Navigate to="/" replace />;
}

function PlatformSettingsIndex() {
  const { loading, has, hasAttribute } = usePermissionsContext();
  if (loading) return <ArtAppLoader />;

  const landing = getPlatformSettingsLanding(has, hasAttribute);
  return landing ? <Navigate to={landing} replace /> : <AccessDenied />;
}

function AgentBuilderRouteAccess({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { loading, error, refresh, has, hasApplication } = usePermissionsContext();
  if (loading) return <ArtAppLoader />;
  if (error) return <ApplicationUnavailable application="AGENT_LAB" hasApplicationAccess={false} permissionError onRetry={refresh} />;
  if (location.pathname === "/agent-builder" || location.pathname === "/agent-builder/") {
    return <Navigate to="/agent-builder/dashboard" replace />;
  }
  if (location.pathname === "/agent-builder/dashboard") {
    if (!hasApplication("AGENT_LAB")) {
      return <ApplicationUnavailable application="AGENT_LAB" hasApplicationAccess={false} />;
    }
    return has("AGENT_LAB", "DASHBOARD", "Read") ? <>{children}</> : <AccessDenied />;
  }
  return hasApplication("AGENT_LAB") || hasApplication("ORCHESTRATOR")
    ? <>{children}</>
    : <AccessDenied />;
}

function LiveConnectRouteAccess({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { loading, error, refresh, has, hasApplication } = usePermissionsContext();
  if (loading) return <ArtAppLoader />;
  if (error) return <ApplicationUnavailable application="LIVE_CONNECT" hasApplicationAccess={false} permissionError onRetry={refresh} />;

  const pathname = location.pathname;
  if (pathname === "/live" || pathname === "/live/") {
    return <Navigate to="/live/dashboard" replace />;
  }
  if (pathname === "/live/dashboard") {
    if (!hasApplication("LIVE_CONNECT")) {
      return <ApplicationUnavailable application="LIVE_CONNECT" hasApplicationAccess={false} />;
    }
    return has("LIVE_CONNECT", "DASHBOARD", "Read") ? <>{children}</> : <AccessDenied />;
  }

  const feature = pathname.startsWith("/live/targeted-channels") ? "TARGETED_CHANNELS"
    : pathname.startsWith("/live/encrypted-channels") ? "SECURE_CHANNELS"
    : pathname.startsWith("/live/broadcast-channels") ? "BROADCAST_CHANNELS"
    : pathname.startsWith("/live/group-channels") ? "GROUP_CHANNELS"
    : pathname.startsWith("/live/shared-object-channels") ? "SHARED_OBJECT_CHANNELS"
    : pathname.startsWith("/live/interceptors") ? "INTERCEPTORS"
    : pathname.startsWith("/live/webhooks") ? "WEBHOOKS"
    : pathname.startsWith("/live/channels/targeted/") ? "TARGETED_CHANNELS"
    : pathname.startsWith("/live/channels/secure/") ? "SECURE_CHANNELS"
    : pathname.startsWith("/live/channels/broadcast/") ? "BROADCAST_CHANNELS"
    : pathname.startsWith("/live/channels/group/") ? "GROUP_CHANNELS"
    : pathname.startsWith("/live/channels/shared-object/") ? "SHARED_OBJECT_CHANNELS"
    : null;

  const allowed = pathname.startsWith("/live/orchestrators")
    ? has("ORCHESTRATOR", "ORCHESTRATION", "Read")
    : feature !== null && has("LIVE_CONNECT", feature, "Read");
  return allowed ? <>{children}</> : <AccessDenied />;
}

// function AppStudioRouteAccess({ children }: { children: ReactNode }) {
//   const { loading, error, refresh, hasApplication } = usePermissionsContext();
//   if (loading) return <ArtAppLoader />;
//   if (error) return <ApplicationUnavailable application="INTERFACE" hasApplicationAccess={false} permissionError onRetry={refresh} />;
//   return hasApplication("INTERFACE")
//     ? <>{children}</>
//     : <ApplicationUnavailable application="INTERFACE" hasApplicationAccess={false} />;
// }

function DatalakeRouteAccess({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { loading, error, refresh, has, hasApplication } = usePermissionsContext();
  if (loading) return <ArtAppLoader />;
  if (error) return <ApplicationUnavailable application="DATALAKE" hasApplicationAccess={false} permissionError onRetry={refresh} />;
  if (location.pathname === "/datalake" || location.pathname === "/datalake/") {
    return <Navigate to="/datalake/overview" replace />;
  }
  const hasApplicationAccess = hasApplication("DATALAKE");
  if (!hasApplicationAccess) {
    return <ApplicationUnavailable application="DATALAKE" hasApplicationAccess={false} />;
  }

  const pathname = location.pathname;
  const requiredFeatures = pathname === "/datalake/overview"
    ? ["DASHBOARD"]
    : pathname.startsWith("/datalake/tables/") && pathname.endsWith("/rows")
      ? ["COLLECTIONS", "RECORDS"]
      : pathname.startsWith("/datalake/tables/") && pathname.endsWith("/schema")
        ? ["COLLECTIONS", "SCHEMA"]
        : pathname.startsWith("/datalake/tables/") && pathname.endsWith("/relations")
          ? ["COLLECTIONS", "RELATIONSHIPS"]
          : pathname.startsWith("/datalake/tables/") && pathname.endsWith("/triggers")
            ? ["COLLECTIONS", "AUTOMATIONS"]
            : pathname.startsWith("/datalake/tables")
              ? ["COLLECTIONS"]
              : pathname.startsWith("/datalake/relations")
                ? ["RELATIONSHIPS"]
                : pathname.startsWith("/datalake/triggers")
                  ? ["AUTOMATIONS"]
                  : pathname.startsWith("/datalake/memory")
                    ? ["MEMORY"]
                    : pathname.startsWith("/datalake/storage")
                      ? ["STORAGE"]
                      : pathname.startsWith("/datalake/audit")
                        ? ["AUDIT"]
                        : [];

  return requiredFeatures.every((feature) => has("DATALAKE", feature, "Read"))
    ? <>{children}</>
    : <AccessDenied />;
}

function SuperAdminRouteAccess({ children }: { children: ReactNode }) {
  const { loading, isSuperAdmin } = usePermissionsContext();
  if (loading) return <ArtAppLoader />;
  if (!isSuperAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function AppManagementGeneralAccess({ children }: { children: ReactNode }) {
  const canManageDefaults = useAttributePermission("APP_MANAGEMENT", "APP_CONFIGURATION", "ManageAppDefaults");
  const canManageAnonymous = useAttributePermission("APP_MANAGEMENT", "APP_CONFIGURATION", "ManageAnonymousConnections");
  const canManageNamespaces = useAttributePermission("APP_MANAGEMENT", "APP_CONFIGURATION", "ManageChannelNamespaces");
  return canManageDefaults || canManageAnonymous || canManageNamespaces ? <>{children}</> : <AccessDenied />;
}

function AppManagementIndex() {
  const { loading, has, hasAttribute } = usePermissionsContext();
  if (loading) return <ArtAppLoader />;
  if (["ManageAppDefaults", "ManageAnonymousConnections", "ManageChannelNamespaces"].some(
    (attribute) => hasAttribute("APP_MANAGEMENT", "APP_CONFIGURATION", attribute),
  )) return <Navigate to="general" replace />;
  if (has("APP_MANAGEMENT", "SESSION_ROLES", "Read")) return <Navigate to="session-roles" replace />;
  if (hasAttribute("APP_MANAGEMENT", "NOTIFICATION_MANAGEMENT", "ManageEmailNotifications")) return <Navigate to="messaging" replace />;
  if (has("APP_MANAGEMENT", "CONNECTIONS", "Read")) return <Navigate to="connections" replace />;
  if (has("APP_MANAGEMENT", "GROUPS", "Read")) return <Navigate to="groups" replace />;
  return <AccessDenied />;
}

function MessagingIndex() {
  const canEmail = useAttributePermission("APP_MANAGEMENT", "NOTIFICATION_MANAGEMENT", "ManageEmailNotifications");
  if (canEmail) return <Navigate to="email" replace />;
  return <Navigate to="/" replace />;
}

function UsageIndex() {
  const canViewUsage = useAttributePermission("PLATFORM_SETTINGS", "BILLING_USAGE", "ViewUsage");
  const canManageAlerts = useAttributePermission("PLATFORM_SETTINGS", "BILLING_USAGE", "ManageUsageAlerts");
  if (canViewUsage) return <Navigate to="overview" replace />;
  if (canManageAlerts) return <Navigate to="alerts" replace />;
  return <Navigate to="/" replace />;
}

function BillingIndex() {
  const canViewBilling = useAttributePermission("PLATFORM_SETTINGS", "BILLING_USAGE", "ViewBilling");
  const canManageAddons = useAttributePermission("PLATFORM_SETTINGS", "BILLING_USAGE", "ManageAddons");
  const canManageDetails = useAttributePermission("PLATFORM_SETTINGS", "BILLING_USAGE", "ManageBillingDetails");
  if (canViewBilling) return <Navigate to="overview" replace />;
  if (canManageAddons) return <Navigate to="addons" replace />;
  if (canManageDetails) return <Navigate to="details" replace />;
  return <Navigate to="/" replace />;
}

function RouteThemeBoundary({
  apiService,
  idpService,
  children,
}: {
  apiService: APIService;
  idpService: APIService;
  children: ReactNode;
}) {
  const location = useLocation();
  const { themeName } = applicationBrandForPath(location.pathname);

  return (
    <ArtServiceProvider
      apiService={apiService}
      idpService={idpService}
      themeName={themeName}
    >
      {children}
    </ArtServiceProvider>
  );
}

const App = () => {
  return (
    <Provider store={ArtStore}>
      <PersistGate loading={null} persistor={persistor}>
        <ArtApplication ArtStore={ArtStore} />
      </PersistGate>
    </Provider>
  );
};

export default App;

interface ArtProps {
  ArtStore: any;
}

export function ArtApplication({ ArtStore }: ArtProps) {
  const apiServiceInstance = useMemo(() => new APIService({
    BASE_URL: ART_URL + "/api",
    ART_STORE: ArtStore,
  }), [ArtStore]);
  const idpServiceInstance = useMemo(() => new APIService({
    BASE_URL: ART_URL + "/idp",
    ART_STORE: ArtStore,
    DisableDefaultHeadder: true,
    DisableDefaultTenantUrl: true,
  }), [ArtStore]);

  const [userData, setUserData] = useState<any>(null);
  const [profileStatus, setProfileStatus] = useState<ProfileBootstrapStatus>("loading");
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);

  const onLoginComplete = () => { };

  const get_user = () => {
    return new Promise((resolve, reject) => {
      const store = apiServiceInstance.getStore();
      const verifyLogin = (profileAttempt = 0) => {
        const state = store.getState();
        const token = state.auth.token;
        if (!token) {
          setTimeout(() => {
            verifyLogin();
          }, 200);
          return;
        }
        apiServiceInstance
          .post("/user/profile", {}, { showErrorSnack: false, showSuccessSnack: false })
          .then(({ data: { data } }: any) => {
            dispatch(setUser(data));
            setUserData(data);
            dispatch(
              updateAppState({
                project_key: data?.selected_project,
                project_name: data?.selected_project_name,
                environment: data?.selected_environment,
                is_invited: data?.is_invited
              })
            );
            dispatch(updateLicenseState(licenseStateFromProfile(data)));
            setProfileStatus("ready");
            resolve(data);
          })
          .catch((err) => {
            const retryDelay = PROFILE_BOOTSTRAP_RETRY_DELAYS_MS[profileAttempt];
            if (retryDelay !== undefined && shouldRetryProfileBootstrapError(err)) {
              setTimeout(() => verifyLogin(profileAttempt + 1), retryDelay);
              return;
            }
            setProfileStatus(classifyProfileBootstrapError(err));
            reject(err);
          });
      };
      verifyLogin();
    });
  };

  return (
    <Router>
      <RouteThemeBoundary
        apiService={apiServiceInstance}
        idpService={idpServiceInstance}
      >
          <SnackbarProvider
            maxSnack={3}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            preventDuplicate={true}
            autoHideDuration={200}
          >
            <GuardProvider
              idp_uri={ART_URL}
              redirect_uri={`${ART_URL}/`}
              onLoginComplete={onLoginComplete}
              get_user={get_user}
            >
              <SnackbarContainer />
            {profileStatus === "access-denied" ? (
              <AccessDenied />
            ) : profileStatus === "failed" ? (
              <AppSwitchProvider>
                <ApplicationUnavailable
                  application="AGENT_LAB"
                  hasApplicationAccess={false}
                  permissionError
                  onRetry={async () => {
                    setProfileStatus("loading");
                    try {
                      await get_user();
                    } catch {
                      // get_user updates the terminal state shown here.
                    }
                  }}
                />
              </AppSwitchProvider>
            ) : !userData || !isAuthenticated ? (
              <ArtAppLoader
                fullScreen
                logoSrc="/assets/images/A-Realtime-Tech.svg"
                message="Hold on, getting things ready..."
              />
            ) : (
              <PermissionsProvider>
                <AppSwitchProvider>
                <Routes>
                  <Route path="/" element={<AuthCheck />} />
                  <Route
                    path="/onboarding"
                    element={
                      <RequirePermission application="GENERAL" feature="ONBOARDING" category="Read">
                        <OnboardingFlow />
                      </RequirePermission>
                    }
                  >
                    <Route path="get-started" element={<GetStarted user_data={userData} />} />
                    <Route path="project-setup" element={<ProjectSetup user_data={userData} />} />
                    <Route path="review" element={<CompleteSetup />} />
                    <Route path="guide" element={<NavGuide />} />
                  </Route>

                  <Route element={<AdminLayout />}>
                    <Route path="/platform-settings">
                      <Route index element={<PlatformSettingsIndex />} />
                      <Route path="organisation" element={<Navigate to="/application-settings/general" replace />} />
                      <Route
                        path="roles-permissions"
                        element={
                          <RequirePermission application="PLATFORM_SETTINGS" feature="ROLE_MANAGEMENT" category="Read">
                            <RolesPermissions />
                          </RequirePermission>
                        }
                      />
                      <Route
                        path="roles-permissions/:id"
                        element={
                          <RequirePermission application="PLATFORM_SETTINGS" feature="ROLE_MANAGEMENT" category="Read">
                            <RoleDetail />
                          </RequirePermission>
                        }
                      >
                        <Route index element={<Navigate to="details" />} />
                        <Route path="details" element={<RoleDetailInfo />} />
                        <Route path="abac" element={<RoleAbac />} />
                        <Route
                          path="resource-access"
                          element={
                            <RequireAttributePermission application="PLATFORM_SETTINGS" feature="ROLE_MANAGEMENT" attribute="ManageRoleScopes">
                              <RoleResourceAccess />
                            </RequireAttributePermission>
                          }
                        />
                      </Route>
                      <Route
                        path="environment"
                        element={
                          <RequirePermission application="PLATFORM_SETTINGS" feature="ENVIRONMENT_MANAGEMENT" category="Read">
                            <ArtEnvironmentSettingsPage />
                          </RequirePermission>
                        }
                      />
                      <Route
                        path="projects"
                        element={
                          <RequirePermission application="PLATFORM_SETTINGS" feature="PROJECT_MANAGEMENT" category="Read">
                            <ArtProjectPage />
                          </RequirePermission>
                        }
                      />
                      <Route
                        path="projects/:projectKey"
                        element={
                          <RequirePermission application="PLATFORM_SETTINGS" feature="PROJECT_MANAGEMENT" category="Read">
                            <RequireAttributePermission application="PLATFORM_SETTINGS" feature="USER_MANAGEMENT" attribute="ViewResourceAccess">
                              <ProjectAccessDetails />
                            </RequireAttributePermission>
                          </RequirePermission>
                        }
                      />
                      <Route
                        path="client"
                        element={
                          <RequirePermission application="PLATFORM_SETTINGS" feature="CLIENT_MANAGEMENT" category="Read">
                            <ApplicationClientSettings />
                          </RequirePermission>
                        }
                      />
                      <Route
                        path="api-keys"
                        element={
                          <RequirePermission application="PLATFORM_SETTINGS" feature="API_KEY_MANAGEMENT" category="Read">
                            <APIKeyManagement />
                          </RequirePermission>
                        }
                      />
                      <Route path="application-users" element={<ApplicationUsers />}>
                        <Route index element={<ApplicationUsersIndex />} />
                        <Route path="users" element={
                          <RequireAttributePermission application="PLATFORM_SETTINGS" feature="USER_MANAGEMENT" attribute="ViewUsers">
                            <ArtUserSettingsPage />
                          </RequireAttributePermission>
                        } />
                        <Route path="invite-users" element={
                          <RequireAttributePermission application="PLATFORM_SETTINGS" feature="USER_MANAGEMENT" attribute="ViewInvitations">
                            <InviteUser />
                          </RequireAttributePermission>
                        } />
                      </Route>
                      <Route path="billing" element={<GeneralPlanSettings />}>
                        <Route index element={<BillingIndex />} />
                        <Route path="overview" element={
                          <RequirePermission application="PLATFORM_SETTINGS" feature="BILLING_USAGE" category="Billing">
                            <ArtBillingOverview />
                          </RequirePermission>
                        } />
                        <Route path="addons" element={
                          <RequirePermission application="PLATFORM_SETTINGS" feature="BILLING_USAGE" category="Add-ons">
                            <AddonsPage />
                          </RequirePermission>
                        } />
                        <Route path="details" element={
                          <RequireAttributePermission application="PLATFORM_SETTINGS" feature="BILLING_USAGE" attribute="ManageBillingDetails">
                            <ArtBillingPreferences />
                          </RequireAttributePermission>
                        } />
                      </Route>
                      <Route path="billing/history" element={
                        <RequirePermission application="PLATFORM_SETTINGS" feature="BILLING_USAGE" category="Invoices">
                          <ArtBillingHistory />
                        </RequirePermission>
                      } />
                      <Route path="usage" element={<GeneralUsage />}>
                        <Route index element={<UsageIndex />} />
                        <Route path="overview" element={
                          <RequirePermission application="PLATFORM_SETTINGS" feature="BILLING_USAGE" category="Usage">
                            <ProductSettingsLoader surface="usage-overview" />
                          </RequirePermission>
                        } />
                        <Route path="platform-limits" element={
                          <RequirePermission application="PLATFORM_SETTINGS" feature="BILLING_USAGE" category="Usage">
                            <PlatformLimits />
                          </RequirePermission>
                        } />
                        <Route path="limits" element={
                          <RequirePermission application="PLATFORM_SETTINGS" feature="BILLING_USAGE" category="Usage">
                            <ProductSettingsLoader surface="usage-limits" />
                          </RequirePermission>
                        } />
                        <Route path="alerts" element={
                          <RequirePermission application="PLATFORM_SETTINGS" feature="BILLING_USAGE" category="Alerts">
                            <AppUsageAlert />
                          </RequirePermission>
                        } />
                      </Route>
                    </Route>
                    <Route path="/application-settings">
                      <Route index element={<AppManagementIndex />} />
                      <Route
                        path="general"
                        element={
                          <AppManagementGeneralAccess>
                            <AppSettings />
                          </AppManagementGeneralAccess>
                        }
                      />
                      <Route
                        path="session-roles"
                        element={
                          <RequirePermission application="APP_MANAGEMENT" feature="SESSION_ROLES" category="Read">
                            <SessionRolesList />
                          </RequirePermission>
                        }
                      />
                      <Route path="messaging" element={<MessagingConfiguration />}>
                        <Route index element={<MessagingIndex />} />
                        <Route path="email" element={
                          <RequireAttributePermission application="APP_MANAGEMENT" feature="NOTIFICATION_MANAGEMENT" attribute="ManageEmailNotifications">
                            <EmailConfigListContent />
                          </RequireAttributePermission>
                        } />
                      </Route>
                      <Route path="email-config" element={<Navigate to="/application-settings/messaging/email" replace />} />
                      <Route path="email-config/oauth/callback" element={<RequireAttributePermission application="APP_MANAGEMENT" feature="NOTIFICATION_MANAGEMENT" attribute="ManageEmailNotifications"><EmailConfigOAuthCallback /></RequireAttributePermission>} />
                      <Route path="email-config/new" element={<RequireAttributePermission application="APP_MANAGEMENT" feature="NOTIFICATION_MANAGEMENT" attribute="ManageEmailNotifications"><EmailConfigEditor /></RequireAttributePermission>} />
                      <Route path="email-config/:projectKey/templates" element={<RequireAttributePermission application="APP_MANAGEMENT" feature="NOTIFICATION_MANAGEMENT" attribute="ManageEmailNotifications"><ProjectTemplates /></RequireAttributePermission>} />
                      <Route path="email-config/:projectKey/templates/:type" element={<RequireAttributePermission application="APP_MANAGEMENT" feature="NOTIFICATION_MANAGEMENT" attribute="ManageEmailNotifications"><EmailTemplateEditor /></RequireAttributePermission>} />
                      <Route path="email-config/:id" element={<RequireAttributePermission application="APP_MANAGEMENT" feature="NOTIFICATION_MANAGEMENT" attribute="ManageEmailNotifications"><EmailConfigEditor /></RequireAttributePermission>} />
                      {/* <Route path="application-users" element={<ArtUserSettingsPage />} /> */}
                      <Route path="usage/*" element={<Navigate to="/platform-settings/usage" replace />} />
                      <Route path="billings/*" element={<Navigate to="/platform-settings/billing" replace />} />
                      {/* Live Connect - host-owned modules (connections, groups, roles) */}
                      <Route
                        path="connections"
                        element={
                          <RequirePermission application="APP_MANAGEMENT" feature="CONNECTIONS" category="Read">
                            <ArtConnectionList />
                          </RequirePermission>
                        }
                      />
                      <Route
                        path="/application-settings/connections/profile-insight/:id"
                        element={
                          <RequirePermission application="APP_MANAGEMENT" feature="CONNECTIONS" category="Read">
                            <ArtProfileInsight />
                          </RequirePermission>
                        }
                      />
                      <Route
                        path="groups"
                        element={
                          <RequirePermission application="APP_MANAGEMENT" feature="GROUPS" category="Read">
                            <GroupList />
                          </RequirePermission>
                        }
                      />
                    </Route>

                    <Route path="/account-settings">
                      <Route path="general" element={<GeneralSettings />}>
                        <Route index element={<Navigate to="personal-profile" replace />} />
                        <Route path="app-settings" element={<Navigate to="/application-settings/general" replace />} />
                        <Route
                          path="personal-profile"
                          element={
                            <RequirePermission application="ACCOUNT_SETTINGS" feature="PROFILE_MANAGEMENT" category="Read">
                              <PersonalProfile />
                            </RequirePermission>
                          }
                        />
                        <Route
                          path="accounts"
                          element={
                            <RequirePermission application="ACCOUNT_SETTINGS" feature="PROFILE_MANAGEMENT" category="Write">
                              <AccountsSettings />
                            </RequirePermission>
                          }
                        />
                        <Route path="linked-devices" element={<LinkedDevices />} />
                        <Route
                          path="workspaces"
                          element={
                            <RequireAttributePermission application="ACCOUNT_SETTINGS" feature="WORKSPACE_MANAGEMENT" attribute="ViewWorkspaces">
                              <Workspaces />
                            </RequireAttributePermission>
                          }
                        />
                      </Route>
                      <Route path="application-users/*" element={<Navigate to="/platform-settings/application-users" replace />} />
                    </Route>

                    {/* External modules */}
                    <Route
                      path="/live/*"
                      element={
                        <LiveConnectRouteAccess>
                          <ArtLiveLoader />
                        </LiveConnectRouteAccess>
                      }
                    />
                    <Route
                      path="/agent-builder/*"
                      element={
                        <AgentBuilderRouteAccess>
                          <ArtAgentBuilderLoader />
                        </AgentBuilderRouteAccess>
                      }
                    />

                    {/* <Route path="/app-studio/*" element={<AppStudioRouteAccess><ArtAppStudioLoader /></AppStudioRouteAccess>} /> */}
                    <Route path="/datalake/*" element={<DatalakeRouteAccess><ArtDatalakeLoader /></DatalakeRouteAccess>} />
                    <Route
                      path="/super-admin/*"
                      element={
                        <SuperAdminRouteAccess>
                          <RequirePermission application="GENERAL" feature="MASTER_API" category="Auth">
                            <ArtSuperAdminLoader />
                          </RequirePermission>
                        </SuperAdminRouteAccess>
                      }
                    />
                    {/* <Route path="/marketplace/cookbook" element={<CookbookMarketplace />} /> */}
                    {/* <Route path="/marketplace/cookbook/:recipeId" element={<RecipeDetail />} /> */}
                  </Route>
                </Routes>
                </AppSwitchProvider>
              </PermissionsProvider>
            )}
            </GuardProvider>
          </SnackbarProvider>
      </RouteThemeBoundary>
    </Router>
  );
}
